// @flow
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useUnmount } from 'react-use';

import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';

import bankIcon from 'assets/nav-menu/accounts.svg';

import StdFormDialog from 'components/Dialogs/StdFormDialog';
import { getTrackingIdFromInstitution } from 'data/institutions/utils';
import withStdForm from 'data/StdForm';

import { getFeatureFlag } from 'utils/featureFlags/selectors';
import { getLogger } from 'utils/logger';
import tracker from 'utils/tracker';

import { isAcme } from 'isAcme';

import Authenticator from './Authenticator';
import PlaidLink from './PlaidLink'
import PlaidLinkTitle from './PlaidLink/title';
import PlaidLanding from './PlaidLanding';
import InstitutionPicker from './InstitutionPicker';
import InstitutionAccountsUpdater from './InstitutionAccountsUpdater';
import NonConnectedAccount from './NonConnectedAccount';
import NonConnectedAccountTitle from './NonConnectedAccount/title';
import BillScoutPresentPage from './BillScoutPresentPage';
import CapOne from './CapOne';
import type { AccountDiscoveryDialogProps } from './types';

import * as actions from './actions';
import * as selectors from './selectors';

import { useStyles } from './styles';

const log = getLogger('AccountDiscovery');

type Props = AccountDiscoveryDialogProps & {
  formId: string,
  stdFormHandleSubmitSuccess: () => void,
  stdFormHandleCancel: () => void,
}

function getParamsBasedOnMode(mode, aggregator) {

  switch (mode) {
    case 'ACCOUNT-REDISCOVERY':
      return {
        cpSetupMode: 'DISCOVER_ACCOUNTS_ONLY',
        fullAddAccountsDialog: true,
        dialogTitle: 'Reconnect Your Account',
        page: (aggregator === 'PLAID') ? 'ACCOUNTS' : 'AUTHENTICATOR',
      };
    case 'UPDATE-CREDENTIALS':
      return {
        cpSetupMode: 'UPDATE_CREDENTIALS',
        fullAddAccountsDialog: false,
        dialogTitle: 'Update Credentials',
        page: 'AUTHENTICATOR',
      };
    case 'RE-AUTHENTICATE':
      return {
        cpSetupMode: 'UPDATE_CREDENTIALS',
        fullAddAccountsDialog: false,
        dialogTitle: 'Re-Authenticate',
        page: 'AUTHENTICATOR',
      };
    case 'PROVIDE-CREDENTIALS':   // TODO: This is going away with EWC-Live
      return {
        cpSetupMode: 'PROVIDE_CREDENTIALS',
        fullAddAccountsDialog: false,
        dialogTitle: 'Provide Credentials',
        page: 'AUTHENTICATOR',
      };
    case 'PROVIDE-MFA':
      return {
        cpSetupMode: 'PROVIDE-MFA',
        fullAddAccountsDialog: false,
        dialogTitle: 'Multi-Factor Authentication',
        page: 'AUTHENTICATOR',
      };
    case 'ADD-FI':
    case 'UPDATE-FI':
    case 'GSF':
    default:
      return {
        cpSetupMode: 'DISCOVER_ACCOUNTS_ONLY',
        fullAddAccountsDialog: true,
        dialogTitle: (aggregator !== 'PLAID') ? 'Add your Account' : (<PlaidLinkTitle/>),
        page: 'SEARCH',
      };
  }
}

function getInitialState(addFiDefaultProvider, mode, institutionLogin, mfaChallenge) {

  let aggregator = 'INTUIT_FDS';
  if (mode === 'ADD-FI' || mode === 'GSF') {
    if (institutionLogin) {
      log.warn('The institutionLogin is ignored when in ADD_FI mode');
      institutionLogin = null;  // eslint-disable-line no-param-reassign
    }
    if (addFiDefaultProvider === 'PLAID') {
      aggregator = 'PLAID';
    }
  } else if (!institutionLogin) {
    log.error('The institutionLogin is required when not in ADD_FI mode');
    // todo: should throw error
  } else if (institutionLogin.aggregators.get(0).channel?.startsWith('PLAID')) {
    aggregator = 'PLAID';
  }
  if (mode !== 'PROVIDE-MFA' && mfaChallenge != null) {
    log.warn('The mfaChallenge is ignored when not in PROVIDE-MFA mode');
    mfaChallenge = null;  // eslint-disable-line no-param-reassign
  }

  const paramsBasedOnMode = getParamsBasedOnMode(mode, aggregator);
  return {
    aggregator,
    mode,
    cpSetupMode: paramsBasedOnMode.cpSetupMode,
    fullAddAccountsDialog: paramsBasedOnMode.fullAddAccountsDialog,
    institutionLogin,
    page: paramsBasedOnMode.page,
    dialogTitle: paramsBasedOnMode.dialogTitle,
    mfaChallenge,
  };
}

function getTrackingAggregatorProperty(aggregator) {
  return aggregator === 'PLAID' ? 'PLAID' : 'INTUIT_FDS';
}

const AccountDiscoveryWizard = (props: Props) => {
  const { afterCloseAction, error, formId, initiator, mode, stdFormHandleSubmitSuccess } = props;
  const dispatch = useDispatch();

  const addFiDefaultProvider = useSelector((state) => getFeatureFlag(state,'addFiDefaultProvider'));
  const initialState = useMemo(
    () => getInitialState(addFiDefaultProvider, mode, props.institutionLogin, props.mfaChallenge),
    [addFiDefaultProvider, mode, props.institutionLogin, props.mfaChallenge]
  );

  const isAuthenticationDone = useSelector(selectors.getIsAuthenticationDone);
  const isAccountUpsertingDone = useSelector(selectors.getIsAccountUpsertingDone);

  const accountDiscoveryData = useSelector(selectors.getAccountDiscoveryData);
  const institution = useSelector(selectors.getInstitution);
  const institutionLogin = useSelector(selectors.getInstitutionLogin);

  const [cpSetupMode, setCpSetupMode] = useState(initialState.cpSetupMode);
  const [dialogTitle, setDialogTitle] = useState(initialState.dialogTitle);
  const [fullAddAccountsDialog, setFullAddAccountsDialog] = useState(initialState.fullAddAccountsDialog);
  const [ isClosing, setIsClosing ] = useState(false);
  const [page, setPage] = useState(initialState.page);
  const [aggregator, setAggregator] = useState(initialState.aggregator);

  const pageRef = useRef();
  const classes = useStyles();

  // initialize state including store state in redux
  const initializeOrReinitializeState = useCallback(() => {
    setCpSetupMode(initialState.cpSetupMode);
    setDialogTitle(initialState.dialogTitle);
    setFullAddAccountsDialog(initialState.fullAddAccountsDialog);
    setPage(initialState.page);
    setAggregator(initialState.aggregator);

    dispatch(actions.accountDiscoverySetup({
      institutionLogin: initialState.institutionLogin,
      mfaChallenge: initialState.mfaChallenge,
    }));
    if (initialState.mode !== 'UPDATE-FI' && initialState.institutionLogin) {
      dispatch(actions.institutionSelected(initialState.institutionLogin.institutionId));
    }
    if (initialState.mode === 'ACCOUNT-REDISCOVERY' && initialState.aggregator === 'PLAID') {
      dispatch(actions.rediscoverInstitutionLogin(initialState.institutionLogin));
    }
    tracker.track(tracker.events.addFIStart, {
      aggregator: getTrackingAggregatorProperty(initialState.aggregator),
      initiator,
      mode,
    });
  }, [dispatch, initialState, initiator, mode]);

  // initialize or re-initialize state (including redux) upon component mount (or when props change, though shouldn't happen)
  useEffect(
    () => {
      log.info('Calling init');
      initializeOrReinitializeState();
    },
    [initializeOrReinitializeState]
  );

  // when unmounting, clean-up temporary store state
  useUnmount(() => () => {
    dispatch(actions.accountDiscoveryReset());
  });

  const trackingProperties = ({
    aggregator: getTrackingAggregatorProperty(aggregator),
    initiator,
    ...(institution && { institution_id: getTrackingIdFromInstitution(institution), institution_name: institution.name }),
    mode,
  });

  const manualTrackingProperties = ({
    ...trackingProperties,
    institution_id: '0',
    institution_name: 'manual',
  });

  switch (page) {
    case ('CAP_ONE'):
    case 'SEARCH':
      if (institution) {
        log.info('AccountDiscovery switching to AUTHENTICATOR page', institution);
        setDialogTitle('Connect to your Bank');
        setPage('AUTHENTICATOR');
      }
      break;
    case 'AUTHENTICATOR':
      if (isAuthenticationDone) {
        if (accountDiscoveryData) {
          log.info('AccountDiscovery switching to ACCOUNTS page', accountDiscoveryData);
          setPage('ACCOUNTS');
          setDialogTitle('Add your Accounts');

          // We track authenticator completed here instead of in the authenticator cause completion isn't done until
          // QCS finishes and returns accountDiscoveryData (in which case - authenticator may never get control again).
          //
          tracker.track(tracker.events.addFIAuthenticated, {
            ...trackingProperties,
            updating: Boolean(accountDiscoveryData.activeAccounts.length || accountDiscoveryData.deadAccounts.length),
          });
        }
        else if (!isClosing) {
          // setIsClosing(true);
          stdFormHandleSubmitSuccess();
        }
      }
      break;
    case 'ACCOUNTS':
      if (isAccountUpsertingDone) {
        log.log('Account Upserting done');
        if (isAcme && mode === 'ADD-FI') {
          setDialogTitle('');
          setPage('BILL_SCOUT');
        }
        else if (!isClosing) {
          stdFormHandleSubmitSuccess();
        }
      }
      break;
    case 'BILL_SCOUT':
      // TODO: Can we move the logic of closing dialog into here when bill scout is completed?
      break;
    case 'ADD_NON_CONNECTED_ACCOUNT':
      if (isAccountUpsertingDone) {
        if (!isClosing) {
          stdFormHandleSubmitSuccess();
        }
      }
      break;
    default:
      break;
  }

  const handleAddNonConnectedAccount = () => {
    setDialogTitle(<NonConnectedAccountTitle />);
    setPage('ADD_NON_CONNECTED_ACCOUNT');
    tracker.track(tracker.events.addFISelect, {
      ...manualTrackingProperties,
    });
  };

  const handleBackOrCancel = () => {
    if ((mode === 'ADD-FI' || mode === 'UPDATE-FI' || mode === 'GSF') && (page !== 'SEARCH' && page !== 'BILL_SCOUT')) {
      tracker.track(tracker.events.addFICancel, {
        ...trackingProperties,
        step: page.toLowerCase(),
        reinitializing: true,
        ...(error && {
          error_code: error.status,
          agg_status_code: error.statusCode,
          // TODO: agg_status_detail: error.statusCodeDetail,
        }),
      });
      initializeOrReinitializeState();
    } else {
      props.stdFormHandleCancel();
    }
  };

  const handleCapOne = () => {
    setAggregator('INTUIT_FDS');
    setPage('CAP_ONE');
    setDialogTitle(<PlaidLinkTitle />);
  };

  const handleClose = () => {
    props.stdFormHandleCancel();
  };

  const handleClosed = (dEvent) => {
    if (pageRef.current) {
      pageRef.current.handleClose(dEvent);
    }

    if (dEvent !== 'SUBMIT_SUCCESS') {
      tracker.track(tracker.events.addFICancel, {
        ...trackingProperties,
        step: page.toLowerCase(),
        ...(error && {
          error_code: error.error_code,
          agg_status_code: error.agg_status_code,
          agg_status_detail: error.agg_status_detail,
        }),
      });
    }

    setIsClosing(true);
    dispatch(actions.accountDiscoveryReset());

    if (afterCloseAction) {
      dispatch(afterCloseAction);
    }
  };

  const handlePlaidStart = () => setPage('AUTHENTICATOR');
  const handleReset = () => initializeOrReinitializeState();

  const shouldDisplayBranding = () =>
    page !== 'SEARCH' && page !== 'ADD_NON_CONNECTED_ACCOUNT' && page !== 'BILL_SCOUT' && institution;

  // if we are in the process of closing, do not render anything
  if (isClosing) {
    return null;
  }

  const renderTitle = () => {

    if (!shouldDisplayBranding()) {
      return dialogTitle;
    }

    let institutionName = institutionLogin ? institutionLogin.name : null;
    if (!institutionName) {
      institutionName = institution ? institution.name : "";
    }

    let iconSrc = null;
    let logoClasses = null;

    if (institution) {
      switch (aggregator) {
        case 'INTUIT_FDS':
          if (institution.logoUrl) {
            iconSrc = institution.logoUrl;
            logoClasses = classes.iconFds;
          }
          break;
        case 'PLAID':
          if (institution.logo) {
            iconSrc = `data:image/svg;base64, ${institution.logo}`;
            logoClasses = classes.iconPlaid;
          }
          break;
        default:
          break;
      }
    }
    if (!iconSrc) {
      iconSrc = bankIcon;
      logoClasses = classes.iconBank;
    }
    const primaryColor = institution ? institution.primaryColor : "#333333";

    return (
      <>
        <div className={classes.colorBar} style={{ backgroundColor: primaryColor }}/>
        <div className={classes.nameLine}>
          <img
            alt="Bank Icon"
            className={logoClasses}
            src={iconSrc}
          />
          <div className={classes.names}>
            <Typography align="left" variant="subtitle1" className={classes.titleName}>
              {dialogTitle}
            </Typography>
            <Typography align="left" variant="h6" className={classes.bankName}>
              {institutionName}&nbsp;
            </Typography>
          </div>
        </div>
      </>
    );
  };


  const renderSearch = () => {
    if (aggregator === 'PLAID') {
      return (<PlaidLanding goPlaid={handlePlaidStart} goManual={handleAddNonConnectedAccount} />);
    }
    return (
      <InstitutionPicker
        onAddUnconnectedAccount={handleAddNonConnectedAccount}
        submitButtonLabel="Continue"
        trackingProperties={trackingProperties}
      />
    );
  };

  const renderAuthenticator = () => {
    if (aggregator === 'PLAID') {
      return (
        <PlaidLink
          onCapOne={handleCapOne}
          onClosed={handleBackOrCancel}
          trackingProperties={trackingProperties}
          updateMode={mode === 'RE-AUTHENTICATE'}
        />
      );
    }
    if (institution) {
      return (
        <Authenticator
          cpSetupMode={cpSetupMode}
          fullAddAccountsDialog={fullAddAccountsDialog}
          provideForRefreshAccounts={mode === 'PROVIDE-CREDENTIALS' || mode === 'PROVIDE-MFA'}
          onCancel={handleBackOrCancel}
          ref={pageRef}
          trackingProperties={trackingProperties}
        />
      );
    }
    return (
      <div className={classes.statusContainer}>
        <CircularProgress size={100} className={classes.progress} />
      </div>
    );
  };

  const renderAccounts = () => {
    if (institution && accountDiscoveryData) {
      return (
        <InstitutionAccountsUpdater
          cancelButtonLabel="Cancel"
          onCancel={handleBackOrCancel}
          onClose={handleClose}
          onReset={handleReset}
          plaid={aggregator === 'PLAID'}
          submitButtonLabel="Submit"
          trackingProperties={trackingProperties}
        />
      );
    }
    return (
      <div className={classes.statusContainer}>
        <CircularProgress size={100} className={classes.progress} />
      </div>
    );
  };

  return (
    <StdFormDialog
      className={classes.dialog}
      dialogProps={{
        classes: {
          paperScrollPaper: fullAddAccountsDialog ? classes.mdPaperScrollPaper : classes.smPaperScrollPaper,
        },
        disableBackdropClick: fullAddAccountsDialog,
        disableEnforceFocus: true,
        disableEscapeKeyDown: fullAddAccountsDialog,
        fullWidth: true,
        hideBackdrop: mode === 'GSF',
        maxWidth: fullAddAccountsDialog ? 'md' : 'sm',
      }}
      disableTitleTypography={shouldDisplayBranding() || page === 'ADD_NON_CONNECTED_ACCOUNT' || page === 'CAP_ONE' || aggregator === 'PLAID'}
      formId={formId}
      onClosed={handleClosed}
      showCloseButton
      spacedButton
      title={renderTitle()}
    >
      {page === 'SEARCH' && renderSearch()}
      {page === 'CAP_ONE' &&
        <CapOne trackingProperties={manualTrackingProperties}/>
      }
      {page === 'AUTHENTICATOR' && renderAuthenticator()}
      {page === 'ACCOUNTS' && renderAccounts()}
      {page === 'ADD_NON_CONNECTED_ACCOUNT' &&
        <NonConnectedAccount
          cancelButtonLabel="Back"
          onCancel={handleBackOrCancel}
          submitButtonLabel="Continue"
          trackingProperties={manualTrackingProperties}
        />
      }
      {page === 'BILL_SCOUT' &&
        <BillScoutPresentPage
          institutionLoginId={institutionLogin.id}
          onClose={handleClose}
          trackingProperties={trackingProperties}
        />
      }
    </StdFormDialog>
  );
};

AccountDiscoveryWizard.defaultProps = {
  mode: 'ADD-FI',
  initiator: 'unknown',

  institutionLogin: null,
};

export default withStdForm({ type: 'ACCOUNT_DISCOVERY_WIZARD' })(AccountDiscoveryWizard);

